import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, ShieldAlert, Sparkles, ChevronRight, Scale, FileText, Lock, RefreshCcw, Mail, Fingerprint, ShieldCheck, Smartphone, KeyRound, Stethoscope, MessageCircle, Download, Send, Bell, BellOff } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, ReferenceArea } from "recharts";
import { listContacts, addContact, deleteContact, triggerSOS, sosHistory, getTrends, updateProfile, getCalendar, logout, downloadHealthReport, sendWeeklyDigest } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { HeartSticker, StarSticker, FlowerSticker, CrescentSticker, RingSticker, SparkBurst } from "../components/DecorativeShapes";
import LegalFooter from "../components/LegalFooter";
import { LEGAL_BUSINESS, LEGAL_BUSINESS_SHORT, LEGAL_CIN } from "./Legal";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PuvioBadge from "../components/PuvioBadge";
import SecuritySheet from "../components/SecuritySheet";
import DevicesSheet from "../components/DevicesSheet";
import RecoverySheet from "../components/RecoverySheet";
import { usePuvioIdentity } from "../genesis/identity/usePuvioIdentity";
import { usePushNotifications } from "../hooks/usePushNotifications";

export default function ProfilePage({ profile, user, onProfileUpdate, onLoggedOut }) {
  const [tab, setTab] = useState("analytics");
  const [contacts, setContacts] = useState([]);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState(null);
  const [phaseDays, setPhaseDays] = useState([]);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newRel, setNewRel] = useState("");
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editCycle, setEditCycle] = useState("28");
  const [securityOpen, setSecurityOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const puvio = usePuvioIdentity({ autoLoad: true });
  const { status: pushStatus, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();

  const load = async () => {
    try {
      const today = new Date();
      const cur = { y: today.getFullYear(), m: today.getMonth() + 1 };
      const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const prv = { y: prev.getFullYear(), m: prev.getMonth() + 1 };
      const [c, h, t, calA, calB] = await Promise.all([
        listContacts(),
        sosHistory(),
        getTrends(30),
        getCalendar(prv.y, prv.m),
        getCalendar(cur.y, cur.m),
      ]);
      setContacts(c); setHistory(h); setTrends(t);
      const allDays = [...(calA?.days || []), ...(calB?.days || [])];
      allDays.sort((a, b) => (a.date < b.date ? -1 : 1));
      setPhaseDays(allDays);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    setEditName(profile?.name || "");
    setEditCycle(String(profile?.average_cycle_length || 28));
  }, [profile]);

  const addCt = async () => {
    if (!newName.trim() || !newPhone.trim()) return;
    try {
      await addContact({ name: newName.trim(), phone: newPhone.trim(), relationship: newRel.trim() || null });
      setNewName(""); setNewPhone(""); setNewRel(""); setOpen(false);
      toast.success("Trusted contact added."); load();
    } catch { toast.error("Could not add contact."); }
  };
  const delCt = async (id) => {
    try { await deleteContact(id); load(); toast.success("Contact removed."); } catch { toast.error("Could not remove."); }
  };
  const fireSOS = async () => {
    try {
      const e = await triggerSOS();
      toast.success(`SOS sent to ${e.contact_count} trusted contact${e.contact_count === 1 ? "" : "s"}.`); load();
    } catch { toast.error("SOS failed. Please call directly."); }
  };
  const saveProfile = async () => {
    try {
      await updateProfile({ name: editName.trim(), average_cycle_length: parseInt(editCycle, 10) });
      toast.success("Profile updated."); setEditOpen(false); onProfileUpdate && onProfileUpdate();
    } catch { toast.error("Could not save."); }
  };

  const doLogout = async () => {
    try {
      await logout();
      toast.success("Signed out.");
      if (onLoggedOut) onLoggedOut();
    } catch { toast.error("Could not sign out."); }
  };

  const doExportPDF = async () => {
    setExporting(true);
    try {
      const blob = await downloadHealthReport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "knya_health_report.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Health report downloaded.");
    } catch { toast.error("Could not generate report. Try again."); }
    finally { setExporting(false); }
  };

  const doSendDigest = async () => {
    setDigestSending(true);
    try {
      await sendWeeklyDigest();
      toast.success("Weekly digest sent to your inbox.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send digest.";
      toast.error(msg);
    }
    finally { setDigestSending(false); }
  };

  const firstName = (profile?.name || "").split(" ")[0];
  const moodData = trends?.mood_scores || [];
  const sleepData = trends?.sleep_hours || [];
  const fitnessData = trends?.fitness_minutes || [];

  /* Build phase bands: contiguous date ranges by tag (period/fertile/ovulation) for overlay.
     Each band has { tag, x1, x2, color }. We project them onto a chart by aligning dates. */
  const PHASE_FILL = {
    period:    "hsla(6, 58%, 56%, 0.18)",
    fertile:   "hsla(142, 32%, 52%, 0.16)",
    ovulation: "hsla(332, 36%, 56%, 0.20)",
  };
  const phaseBands = (() => {
    if (!phaseDays || phaseDays.length === 0) return [];
    const bands = [];
    let current = null;
    phaseDays.forEach((d) => {
      if (d.tag && PHASE_FILL[d.tag]) {
        if (current && current.tag === d.tag) {
          current.x2 = d.date;
        } else {
          if (current) bands.push(current);
          current = { tag: d.tag, x1: d.date, x2: d.date };
        }
      } else {
        if (current) bands.push(current);
        current = null;
      }
    });
    if (current) bands.push(current);
    return bands;
  })();

  /* Reusable phase-band renderer. Recharts requires bands rendered as children of the chart. */
  const renderPhaseBands = (yMin, yMax) =>
    phaseBands.map((b, i) => (
      <ReferenceArea
        key={`${b.tag}-${i}`}
        x1={b.x1}
        x2={b.x2}
        y1={yMin}
        y2={yMax}
        fill={PHASE_FILL[b.tag]}
        stroke="transparent"
        ifOverflow="extendDomain"
      />
    ));

  return (
    <div className="kanya-page-pad-bottom" data-testid="profile-page">
      {/* Pink avatar hero */}
      <div className="px-4 pt-5">
        <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} className="card-pink-hero relative overflow-hidden rounded-[28px] p-5">
          <HeartSticker size={60} color="rgba(255,255,255,0.25)" className="absolute -top-2 -right-2 float-slow"/>
          <StarSticker size={30} color="#F5D867" className="absolute top-6 right-20 float-mid"/>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-white/25 backdrop-blur flex items-center justify-center font-display text-[36px]" style={{ fontWeight: 600 }}>
              {firstName ? firstName[0]?.toUpperCase() : "K"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.22em] opacity-80 font-semibold">Profile</p>
              <p className="font-display text-[32px] leading-none mt-1" style={{ fontWeight: 600 }}>{firstName || "You"}</p>
              <p className="text-sm text-white/85 mt-1.5">Cycle length: {profile?.average_cycle_length || 28} days</p>
            </div>
          </div>
          <div className="mt-4 flex gap-2 items-center flex-wrap">
            <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)} className="rounded-full bg-white text-foreground hover:bg-white/90" data-testid="profile-edit-button">Edit profile</Button>
            {puvio.user?.puvio_user_id && (
              <PuvioBadge
                puvioId={puvio.user.puvio_user_id}
                level={puvio.user?.ecosystem_identity?.verification_status?.level}
              />
            )}
          </div>
        </motion.div>
      </div>

      <div className="px-4 mt-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-3 bg-[hsl(var(--card))] p-1.5 rounded-full border border-[hsl(var(--border))]">
            <TabsTrigger value="analytics" data-testid="profile-tab-analytics" className="rounded-full data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white">Trends</TabsTrigger>
            <TabsTrigger value="safety" data-testid="profile-tab-safety" className="rounded-full data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white">Safety</TabsTrigger>
            <TabsTrigger value="settings" data-testid="profile-tab-settings" className="rounded-full data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white">Settings</TabsTrigger>
          </TabsList>

          {/* TRENDS */}
          <TabsContent value="analytics" className="mt-3 space-y-3">
            <div className="bento">              <div className="b-12 card-mint rounded-[24px] p-4 relative overflow-hidden" data-testid="trends-mood-card">
                <FlowerSticker size={42} color="rgba(255,255,255,0.55)" className="absolute top-3 right-3 float-slow"/>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Mood · 30 days</p>
                <div className="h-28 mt-3">
                  {moodData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={moodData}>
                        <XAxis dataKey="date" hide /><YAxis domain={[1, 5]} hide />
                        {renderPhaseBands(1, 5)}
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid hsl(var(--border))", borderRadius: 16 }} />
                        <Line type="monotone" dataKey="score" stroke="hsl(var(--plum))" strokeWidth={3} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm opacity-70">Log mood to see your trend.</p>
                  )}
                </div>
                {phaseBands.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2.5 text-[9px] font-semibold uppercase tracking-wider opacity-80">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "hsla(6, 58%, 56%, 0.55)" }} /> Period</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "hsla(142, 32%, 52%, 0.55)" }} /> Fertile</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: "hsla(332, 36%, 56%, 0.65)" }} /> Ovulation</span>
                  </div>
                )}
              </div>

              <div className="b-6 card-lavender rounded-[24px] p-4 relative overflow-hidden" data-testid="trends-sleep-card">
                <CrescentSticker size={36} color="rgba(255,255,255,0.55)" className="absolute top-3 right-3 float-mid"/>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Sleep avg</p>
                <p className="metric-xl text-[44px] mt-2">{trends?.avg_sleep || 0}<span className="text-xl ml-1 opacity-70">h</span></p>
                <div className="h-12 mt-1">
                  {sleepData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sleepData}>
                        <Bar dataKey="hours" fill="rgba(255,255,255,0.7)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : null}
                </div>
              </div>

              <div className="b-6 card-butter rounded-[24px] p-4 relative overflow-hidden" data-testid="trends-cycle-card">
                <StarSticker size={34} color="rgba(255,255,255,0.65)" className="absolute top-3 right-3 float-slow"/>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Avg cycle</p>
                <p className="metric-xl text-[44px] mt-2">{trends?.avg_cycle_length || profile?.average_cycle_length || "—"}<span className="text-xl ml-1 opacity-70">d</span></p>
                <p className="text-xs opacity-70 mt-1">{trends?.cycle_lengths?.length || 0} cycles tracked</p>
              </div>

              <div className="b-12 card-sky rounded-[24px] p-4 relative overflow-hidden" data-testid="trends-fitness-card">
                <SparkBurst size={36} color="rgba(255,255,255,0.7)" className="absolute top-3 right-3 float-mid"/>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Movement · 30 days</p>
                <div className="h-24 mt-2">
                  {fitnessData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={fitnessData}>
                        <XAxis dataKey="date" hide />
                        <YAxis hide />
                        {renderPhaseBands(0, "dataMax")}
                        <Bar dataKey="minutes" fill="rgba(255,255,255,0.85)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm opacity-70">No movement logged.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Export + Digest row */}
            <div className="grid grid-cols-2 gap-2.5">
              <button
                onClick={doExportPDF}
                disabled={exporting}
                className="flex items-center gap-2.5 bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))] transition rounded-[20px] p-4 border border-[hsl(var(--border))] text-left"
                data-testid="profile-export-pdf-button"
              >
                <div className="w-9 h-9 rounded-full surface-mint flex items-center justify-center shrink-0">
                  <Download size={15} />
                </div>
                <div className="min-w-0">
                  <p className="text-[12px] font-bold">Health report</p>
                  <p className="text-[10px] opacity-60">{exporting ? "Generating…" : "Download PDF"}</p>
                </div>
              </button>
              <button
                onClick={doSendDigest}
                disabled={digestSending}
                className="flex items-center gap-2.5 bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))] transition rounded-[20px] p-4 border border-[hsl(var(--border))] text-left"
                data-testid="profile-send-digest-button"
              >
                <div className="w-9 h-9 rounded-full surface-blush flex items-center justify-center shrink-0">
                  <Send size={15} />
                </div>
                <div className="min-w-0">
                  <p className="text-[12px] font-bold">Weekly digest</p>
                  <p className="text-[10px] opacity-60">{digestSending ? "Sending…" : "Send to email"}</p>
                </div>
              </button>
            </div>
          </TabsContent>
          <TabsContent value="safety" className="mt-3 space-y-3">
            <div className="card-cream rounded-[28px] p-5 relative overflow-hidden" data-testid="safety-card">
              <RingSticker size={48} color="hsl(var(--sos))" className="absolute top-3 right-3 float-mid"/>
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Safety</p>
              <p className="font-display text-[26px] leading-tight mt-1" style={{ fontWeight: 600 }}>Calm, immediate help</p>
              <p className="text-sm opacity-80 mt-1">For emergencies, please call your local emergency number directly.</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="w-full h-12 mt-4 rounded-full text-base" style={{ background: "hsl(var(--sos))", color: "hsl(var(--sos-foreground))" }} data-testid="profile-sos-button">
                    <ShieldAlert size={18} className="mr-2" /> Send SOS
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send an SOS?</AlertDialogTitle>
                    <AlertDialogDescription>
                      We'll notify your {contacts.length} trusted contact{contacts.length === 1 ? "" : "s"}. If this is a medical emergency, call your local emergency number directly.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="sos-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={fireSOS} data-testid="sos-confirm">Send SOS</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="trusted-contacts-card">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">Trusted contacts</p>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button variant="secondary" size="sm" className="rounded-full" data-testid="profile-add-trusted-contact-button"><Plus size={14} className="mr-1" /> Add</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                      <DialogTitle className="font-display text-[26px]" style={{ fontWeight: 600 }}>Add a trusted contact</DialogTitle>
                      <DialogDescription>They'll receive your SOS messages.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <Input placeholder="Full name" value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-2xl h-12" data-testid="contact-name-input" />
                      <Input placeholder="Phone number" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} className="rounded-2xl h-12" data-testid="contact-phone-input" />
                      <Input placeholder="Relationship (optional)" value={newRel} onChange={(e) => setNewRel(e.target.value)} className="rounded-2xl h-12" data-testid="contact-relationship-input" />
                    </div>
                    <DialogFooter>
                      <Button variant="secondary" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                      <Button onClick={addCt} className="rounded-full" data-testid="contact-save-button">Save</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="mt-3">
                {contacts.length === 0 && <p className="text-sm text-[hsl(var(--muted-foreground))] py-3">No contacts yet. Add someone you trust.</p>}
                {contacts.map((c) => (
                  <div key={c.id} className="py-3 flex items-center justify-between gap-2 border-b border-[hsl(var(--border))] last:border-0" data-testid={`contact-row-${c.id}`}>
                    <div>
                      <p className="font-semibold">{c.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{c.phone}{c.relationship ? ` • ${c.relationship}` : ""}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => delCt(c.id)} data-testid={`contact-delete-${c.id}`}><Trash2 size={16} /></Button>
                  </div>
                ))}
              </div>
            </div>

            {history.length > 0 && (
              <div className="bg-[hsl(var(--card))] rounded-[24px] p-4" data-testid="sos-history-card">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70 mb-1">Recent SOS</p>
                {history.slice(0, 5).map((h) => (
                  <div key={h.id} className="py-2 text-sm">
                    <p>Sent to {h.contact_count} contact{h.contact_count === 1 ? "" : "s"}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(h.created_at, "MMM d, h:mm a")}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-3 space-y-3">
            {user && (
              <div className="surface-ivory plush-shadow-sm rounded-[24px] p-4 flex items-center gap-3" data-testid="settings-account-card">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-[hsl(var(--border))]" />
                ) : (
                  <div className="w-12 h-12 rounded-full surface-plum flex items-center justify-center text-[hsl(var(--ivory))] font-editorial text-[20px]">
                    {(user.name || user.email || "K").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{user.name || "You"}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{user.email}</p>
                </div>
                <button onClick={doLogout} className="btn-pill-cream !h-9 !px-4 text-[12px]" data-testid="profile-logout-button">
                  Sign out
                </button>
              </div>
            )}

            {/* Push notifications toggle */}
            <div className="surface-card border border-border rounded-[24px] p-4" data-testid="settings-push-card">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-xl bg-orange-50 text-orange-400 flex items-center justify-center">
                  <Bell size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[14px]">Push notifications</p>
                  <p className="text-[12px] text-muted-foreground">Period reminders, streak milestones, weekly digest nudges</p>
                </div>
                <button
                  onClick={pushStatus === "subscribed" ? unsubscribePush : subscribePush}
                  className={`text-[12px] font-bold rounded-full px-3 py-1.5 border transition ${
                    pushStatus === "subscribed"
                      ? "bg-orange-50 border-orange-200 text-orange-500"
                      : "bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white"
                  }`}
                  data-testid="settings-push-toggle"
                >
                  {pushStatus === "subscribed" ? "On" : pushStatus === "denied" ? "Blocked" : "Enable"}
                </button>
              </div>
            </div>

            {/* Library shortcut */}
            <div className="surface-card border border-border rounded-[24px] p-4" data-testid="settings-library-card">
              <Link to="/library" className="flex items-center gap-3" data-testid="settings-open-library">
                <span className="h-9 w-9 rounded-xl bg-blush text-primary flex items-center justify-center">
                  <FileText size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-extrabold text-primary">Library</p>
                  <p className="font-bold text-[14px]">Read with Knya</p>
                  <p className="text-[12px] text-muted-foreground font-medium leading-relaxed">Quiet, evidence-aware reads on your body, mind & rhythm.</p>
                </div>
                <ChevronRight size={16} className="opacity-50" />
              </Link>
            </div>

            {/* Care directory shortcut */}
            <div className="surface-card border border-border rounded-[24px] p-4" data-testid="settings-care-card">
              <Link to="/care" className="flex items-center gap-3" data-testid="settings-open-care">
                <span className="h-9 w-9 rounded-xl bg-aquamarine-soft text-aquamarine flex items-center justify-center">
                  <Stethoscope size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-extrabold text-primary">Care</p>
                  <p className="font-bold text-[14px]">Find a specialist</p>
                  <p className="text-[12px] text-muted-foreground font-medium leading-relaxed">Curated gynecologists, endocrinologists & PCOS coaches near you.</p>
                </div>
                <ChevronRight size={16} className="opacity-50" />
              </Link>
            </div>

            {/* Community shortcut */}
            <div className="surface-card border border-border rounded-[24px] p-4" data-testid="settings-community-card">
              <Link to="/community" className="flex items-center gap-3" data-testid="settings-open-community">
                <span className="h-9 w-9 rounded-xl bg-blush text-primary flex items-center justify-center">
                  <MessageCircle size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-extrabold text-primary">Community</p>
                  <p className="font-bold text-[14px]">You're not alone</p>
                  <p className="text-[12px] text-muted-foreground font-medium leading-relaxed">Anonymous, gently moderated space to ask, share and feel heard.</p>
                </div>
                <ChevronRight size={16} className="opacity-50" />
              </Link>
            </div>

            {/* Genesis / Puvio ID Security */}
            <div className="surface-card border border-border rounded-[24px] p-4" data-testid="settings-genesis-card">
              <div className="flex items-center gap-2">
                <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Fingerprint size={16} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wider font-extrabold text-primary">Puvio ID · Genesis Layer</p>
                  <p className="font-bold text-[14px]">Identity, security & devices</p>
                </div>
              </div>
              <p className="text-[12px] text-muted-foreground mt-2 font-medium leading-relaxed">
                Manage how you sign in, what devices have access, and how the ecosystem reaches you.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2">
                <button
                  onClick={() => setSecurityOpen(true)}
                  className="flex items-center justify-between rounded-2xl bg-background border border-border hover:border-primary/40 hover:bg-muted/40 transition px-3.5 py-3 text-left"
                  data-testid="settings-open-security"
                >
                  <span className="flex items-center gap-2.5">
                    <ShieldCheck size={15} className="text-primary" />
                    <span className="text-[13px] font-bold">Security settings</span>
                  </span>
                  <ChevronRight size={14} className="opacity-50" />
                </button>
                <button
                  onClick={() => setDevicesOpen(true)}
                  className="flex items-center justify-between rounded-2xl bg-background border border-border hover:border-primary/40 hover:bg-muted/40 transition px-3.5 py-3 text-left"
                  data-testid="settings-open-devices"
                >
                  <span className="flex items-center gap-2.5">
                    <Smartphone size={15} className="text-primary" />
                    <span className="text-[13px] font-bold">Device management</span>
                  </span>
                  <ChevronRight size={14} className="opacity-50" />
                </button>
                <button
                  onClick={() => setRecoveryOpen(true)}
                  className="flex items-center justify-between rounded-2xl bg-background border border-border hover:border-primary/40 hover:bg-muted/40 transition px-3.5 py-3 text-left"
                  data-testid="settings-open-recovery"
                >
                  <span className="flex items-center gap-2.5">
                    <KeyRound size={15} className="text-primary" />
                    <span className="text-[13px] font-bold">Account recovery</span>
                  </span>
                  <ChevronRight size={14} className="opacity-50" />
                </button>
              </div>
            </div>
            <div className="card-cream rounded-[24px] p-4 relative overflow-hidden" data-testid="settings-card">
              <FlowerSticker size={36} color="#FF8FB8" className="absolute top-3 right-3 float-slow"/>
              <p className="text-[11px] uppercase tracking-[0.22em] font-bold opacity-70">About Knya</p>
              <p className="text-sm mt-2 leading-relaxed">Knya is a calm women's wellness companion. We never share your data. Predictions and AI insights are not medical advice.</p>
              <p className="text-xs opacity-70 mt-3">Version 1.0.0 • v1 single-user demo</p>
            </div>
            <div className="surface-rose plush-shadow-sm rounded-[24px] p-5 relative overflow-hidden">
              <StarSticker size={32} color="#E9B86A" className="absolute top-3 right-3 float-mid"/>
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-65">Quiet philosophy</p>
              <p className="font-editorial text-[22px] leading-tight mt-2">Your body translated into intelligence.</p>
              <p className="text-sm mt-2 leading-relaxed opacity-80 font-body">Knya doesn't just track — it interprets, predicts, and guides, so daily life moves with your hormones, not against them.</p>
            </div>

            {/* Legal & company */}
            <div className="surface-ivory plush-shadow-sm rounded-[24px] p-4 relative overflow-hidden border border-[hsl(var(--border))]" data-testid="settings-legal-card">
              <p className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-60">Legal & company</p>
              <div className="mt-3 flex items-start gap-3" data-testid="settings-legal-owner">
                <span className="inline-flex w-8 h-8 rounded-full surface-plum items-center justify-center shrink-0">
                  <Scale size={14} className="text-[hsl(var(--ivory))]" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] opacity-60 font-semibold">Business</p>
                  <p className="font-semibold text-sm mt-0.5">{LEGAL_BUSINESS_SHORT}</p>
                  <p className="text-[12px] opacity-70">CIN: <span className="font-semibold">{LEGAL_CIN}</span></p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { to: "/legal?tab=about",   label: "About",    Icon: FileText, testid: "settings-legal-about" },
                  { to: "/legal?tab=terms",   label: "Terms",    Icon: Scale,    testid: "settings-legal-terms" },
                  { to: "/legal?tab=privacy", label: "Privacy",  Icon: Lock,     testid: "settings-legal-privacy" },
                  { to: "/legal?tab=refund",  label: "Refunds",  Icon: RefreshCcw, testid: "settings-legal-refund" },
                  { to: "/legal?tab=contact", label: "Contact",  Icon: Mail,     testid: "settings-legal-contact" },
                ].map(({ to, label, Icon, testid }) => (
                  <Link
                    key={to}
                    to={to}
                    data-testid={testid}
                    className="flex items-center gap-2 bg-[hsl(var(--background))] hover:bg-[hsl(var(--muted))] transition rounded-full px-3 py-2 border border-[hsl(var(--border))] text-[12px] font-semibold"
                  >
                    <Icon size={13} className="text-[hsl(var(--plum))]" /> {label}
                    <ChevronRight size={12} className="ml-auto opacity-50" />
                  </Link>
                ))}
              </div>
            </div>

            <LegalFooter />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit profile dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-display text-[26px]" style={{ fontWeight: 600 }}>Edit profile</DialogTitle>
            <DialogDescription>Update your name and cycle length.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-2xl h-12" data-testid="profile-edit-name" />
            <Input type="number" min="21" max="40" value={editCycle} onChange={(e) => setEditCycle(e.target.value)} className="rounded-2xl h-12" data-testid="profile-edit-cycle" />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={saveProfile} className="rounded-full" data-testid="profile-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Genesis / Puvio sheets */}
      <SecuritySheet
        open={securityOpen}
        onClose={() => setSecurityOpen(false)}
        onOpenDevices={() => { setSecurityOpen(false); setTimeout(() => setDevicesOpen(true), 200); }}
        onOpenRecovery={() => { setSecurityOpen(false); setTimeout(() => setRecoveryOpen(true), 200); }}
      />
      <DevicesSheet open={devicesOpen} onClose={() => setDevicesOpen(false)} />
      <RecoverySheet open={recoveryOpen} onClose={() => setRecoveryOpen(false)} />
    </div>
  );
}
